import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { GridColDef } from '@mui/x-data-grid-pro';

import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { formatCurrency, getOptions, gridDateTimeFormatter } from '@shared/commonFunctions';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { CommonButtonWrapper, Width180, Wrapper } from '@styles/CommonStyles';

import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { requestCreateRule, requestDeleteRule, requestUpdateRule } from './requests';
import { ProductType, ProviderType, RuleType } from './types';
import { ProductRulesModal } from './components/ProductRulesModal';

const columns: GridColDef[] = [
  dataGridCol('startDate', {
    headerName: 'From',
    minWidth: 180,
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('endDate', {
    headerName: 'To',
    minWidth: 180,
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('assetTypes', {
    headerName: 'Asset Type',
  }),
  dataGridCol('planName'),
  dataGridCol('ilendingCostAmount', {
    headerName: 'Hello Refi Cost Amount',
    ...gridCurrencyCell,
  }),
  dataGridCol('ilendingCostFormula', {
    headerName: 'Hello Refi Cost Formula',
  }),
  dataGridCol('packAmount', {
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('packFormula'),
  dataGridCol('markupAmount', gridCurrencyCell),
  dataGridCol('markupFormula'),
  dataGridCol('state'),
  dataGridCol('minTerm'),
  dataGridCol('maxTerm'),
  dataGridCol('minMileage'),
  dataGridCol('maxMileage'),
  dataGridCol('minYear'),
  dataGridCol('maxYear'),
].map((column) => ({ minWidth: 100, flex: 1, ...column }));

export const ProductRules = () => {
  const [currentRule, setCurrentRule] = useState<RuleType | null>(null);
  const [productRulesModalPosition, setProductRulesModalPosition] = useState(0);
  const [providerId, setProviderId] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [productTypeArr, setProductTypeArr] = useState<ProductType[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const { data: providers = [] } = useSWR<ProviderType[]>('/product-service/providers');
  const { data: productRules = [] } = useSWR<RuleType[]>(
    providerId && productTypeId && `/product-service/providers/${providerId}/productTypes/${productTypeId}/rules`
  );

  const productTypes = useMemo(
    () => providers?.find((item: ProviderType) => item.id === providerId)?.productTypes,
    [providers, providerId]
  );

  const gridActions = useMemo(
    () => ({
      onDelete: (data: RuleType) => requestDeleteRule(data),
      onEdit: (data: RuleType) => {
        setCurrentRule({ ...data });
        setIsEditMode(true);
        setProductRulesModalPosition(prevState => prevState + 1);
      },
    }),
    [requestDeleteRule]
  );

  const handleAddButtonClick = () => {
    setCurrentRule(null);
    setProductRulesModalPosition(prevState => prevState + 1);
    setIsEditMode(false);
  };

  const handleSubmit = useCallback(
    async (data: RuleType) => {
      if (isEditMode) {
        await requestUpdateRule(providerId, productTypeId, { ...data });
      } else {
        await requestCreateRule(data);
      }

      setProductRulesModalPosition(0);
    },
    [requestUpdateRule, requestCreateRule, isEditMode, productTypeId, providerId]
  );

  useEffect(() => {
    setProductTypeId('');
    setProductTypeArr(productTypes || []);
  }, [providerId]);

  const subHeader = (
    <CommonButtonWrapper
      gap="10px"
      marginBottom="20px"
      style={{ justifyContent: 'flex-start' }}
    >
      <Width180>
        <SelectAutocomplete
          label="Provider"
          value={providerId}
          options={getOptions(providers)}
          onChange={(value: string) => setProviderId(value)}
        />
      </Width180>

      <Width180>
        <SelectAutocomplete
          label="Product"
          value={productTypeId}
          options={getOptions(productTypeArr || [])}
          onChange={(value: string) => setProductTypeId(value)}
        />
      </Width180>
    </CommonButtonWrapper>
  );

  return (
    <Wrapper>
      <ProductRulesModal
        productRulesModalPosition={productRulesModalPosition}
        setProductRulesModalPosition={setProductRulesModalPosition}
        handleSubmit={handleSubmit}
        currentRule={currentRule}
        providers={providers}
        productTypes={productTypeArr}
        providerId={providerId}
        onChangeProvider={setProviderId}
      />

      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        name="Product rule"
        pageSize={10}
        columns={columns}
        loading={!productRules}
        rows={productRules || []}
        actions={gridActions}
        buttonTitle="Add new rule"
        withSubheader={subHeader}
        onButtonClick={handleAddButtonClick}
      />
    </Wrapper>
  );
};
